import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const SITE_URL = "https://petrocontrol.mx";

function setMetaTag(attr, attrValue, content) {
  let el = document.querySelector(`meta[${attr}="${attrValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkTag(rel, href, extra = {}) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

const PAGE_SEO = {
  "/": {
    title: (fn) => `${fn} — Permisos de Autoconsumo CNE | Controles Volumétricos SAT | México`,
    description: (fn) =>
      `${fn} — Gestión integral de permisos de autoconsumo CNE, controles volumétricos SAT y suministro de combustible. Más de 120 permisos tramitados con 98% de éxito en México.`,
    keywords:
      "permisos autoconsumo CNE, controles volumétricos SAT, suministro combustible México, permiso autoconsumo diesel, regulación energética México, estación combustible propia, permiso CNE, reforma energética, gestión permisos hidrocarburos, PETROCONTROL, comercializador autorizado, reportes obligatorios SAT",
  },
  "/services/gestion-integral": {
    title: (fn) => `Gestión Integral de Regulación Energética — ${fn}`,
    description: (fn) =>
      `Servicio completo de ${fn}: permisos de autoconsumo CNE, controles volumétricos SAT, reportes obligatorios y suministro de combustible con comercializadores autorizados.`,
    keywords:
      "gestión integral energética, permisos autoconsumo, controles volumétricos, suministro combustible, regulación energética completa, PETROCONTROL",
  },
  "/services/permisos-autoconsumo": {
    title: (fn) => `Permisos de Autoconsumo CNE — ${fn}`,
    description: (fn) =>
      `${fn} tramita tu permiso de autoconsumo ante la CNE para operar tu estación de combustible. Diesel, gasolina. Regularización y renovación de permisos.`,
    keywords:
      "permiso autoconsumo CNE, permiso autoconsumo diesel, permiso autoconsumo gasolina, trámite CNE, regularización permisos, renovación permiso autoconsumo",
  },
  "/services/controles-volumetricos": {
    title: (fn) => `Controles Volumétricos SAT — ${fn}`,
    description: (fn) =>
      `${fn} implementa sistemas de controles volumétricos certificados para cumplimiento SAT. Reportes mensuales, certificación de equipos y auditorías.`,
    keywords:
      "controles volumétricos SAT, sistema medición certificado, reportes mensuales SAT, certificación equipos volumétricos, auditoría cumplimiento SAT",
  },
  "/services/suministro-combustible": {
    title: (fn) => `Suministro de Combustible con Comercializadores Autorizados CNE — ${fn}`,
    description: (fn) =>
      `${fn} conecta tu empresa con comercializadores de combustible autorizados por la CNE. Diesel y gasolina a precios competitivos, facturación conforme.`,
    keywords:
      "suministro combustible autorizado, comercializador autorizado CNE, comprar diesel autorizado, suministro gasolina legal, proveedor combustible CNE",
  },
  // URLs SEO amigables (redirigen pero Google las indexa con estos meta tags)
  "/permiso-autoconsumo": {
    title: (fn) => `Permiso de Autoconsumo CNE en México — ${fn}`,
    description: (fn) =>
      `Tramita tu permiso de autoconsumo ante la CNE con ${fn}. Gestión completa para operar estaciones de combustible: diesel y gasolina. Más de 120 permisos aprobados.`,
    keywords:
      "permiso autoconsumo, permiso autoconsumo CNE, trámite permiso autoconsumo, permiso estación combustible, permiso diesel autoconsumo México",
  },
  "/controles-volumetricos": {
    title: (fn) => `Controles Volumétricos SAT para Empresas en México — ${fn}`,
    description: (fn) =>
      `${fn} implementa controles volumétricos certificados para cumplir con el SAT. Sistemas de medición, reportes mensuales y auditorías para permisionarios de hidrocarburos.`,
    keywords:
      "controles volumétricos, controles volumétricos SAT, sistema volumétrico certificado, reportes SAT hidrocarburos, obligaciones fiscales combustible",
  },
  "/servicios": {
    title: (fn) => `Servicios de Regulación Energética — ${fn}`,
    description: (fn) =>
      `Conoce todos los servicios de ${fn}: permisos de autoconsumo CNE, controles volumétricos SAT, suministro de combustible y gestión integral para empresas del sector energético.`,
    keywords:
      "servicios regulación energética, servicios permisos CNE, servicios controles volumétricos, gestión energética México, PETROCONTROL servicios",
  },
  "/contacto": {
    title: (fn) => `Contacto — ${fn} | Diagnóstico Gratuito`,
    description: (fn) =>
      `Contacta a ${fn} para un diagnóstico gratuito de tu situación regulatoria. Permisos de autoconsumo CNE, controles volumétricos SAT. Respuesta en 24 horas.`,
    keywords:
      "contacto PETROCONTROL, diagnóstico gratuito regulación energética, consulta permisos CNE, asesoría controles volumétricos, contacto regulación energética México",
  },
};

function getPageSeo(pathname, firmName) {
  // Try exact match first, then prefix match for /services/
  let config = PAGE_SEO[pathname];
  if (!config) {
    // Check service pages generically
    if (pathname.startsWith("/services/")) {
      config = {
        title: (fn) => `Servicios de Regulación Energética — ${fn}`,
        description: (fn) =>
          `${fn} ofrece servicios especializados de regulación energética para empresas del sector hidrocarburos en México.`,
        keywords: "servicios regulación energética, PETROCONTROL, permisos CNE, controles volumétricos",
      };
    } else if (pathname === "/admin") {
      return { title: `Panel Administrativo — ${firmName}`, description: "", keywords: "", noIndex: true };
    } else if (pathname.startsWith("/portal") || pathname.startsWith("/diagnostico")) {
      return { title: `Portal de Clientes — ${firmName}`, description: "", keywords: "", noIndex: true };
    } else {
      config = PAGE_SEO["/"];
    }
  }
  return {
    title: config.title(firmName),
    description: config.description(firmName),
    keywords: config.keywords,
    noIndex: false,
  };
}

export default function SEOHead({ title, description }) {
  const settings = useSiteSettings();
  const { pathname } = useLocation();
  const firmName = settings?.firm_name || "PETROCONTROL";
  const phone = settings?.phone || "+52 (55) 1234-5678";
  const email = settings?.email || "contacto@petrocontrol.mx";
  const address1 = settings?.address_line1 || "Av. Paseo de la Reforma 250, Piso 15";
  const address2 = settings?.address_line2 || "Ciudad de México, CDMX 06600";

  useEffect(() => {
    const seo = getPageSeo(pathname, firmName);
    const pageTitle = title || seo.title;
    const pageDesc = description || seo.description;
    const pageUrl = `${SITE_URL}${pathname === "/" ? "" : pathname}`;

    // Basic meta
    document.title = pageTitle;
    setMetaTag("name", "description", pageDesc);
    if (seo.keywords) setMetaTag("name", "keywords", seo.keywords);
    setMetaTag("name", "robots", seo.noIndex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large");

    // Canonical
    setLinkTag("canonical", pageUrl);

    // Open Graph
    setMetaTag("property", "og:title", pageTitle);
    setMetaTag("property", "og:description", pageDesc);
    setMetaTag("property", "og:url", pageUrl);
    setMetaTag("property", "og:type", "website");
    setMetaTag("property", "og:image", `${SITE_URL}/logo-petrocontrol.png`);
    setMetaTag("property", "og:locale", "es_MX");
    setMetaTag("property", "og:site_name", firmName);

    // Twitter Card
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", pageTitle);
    setMetaTag("name", "twitter:description", pageDesc);
    setMetaTag("name", "twitter:image", `${SITE_URL}/logo-petrocontrol.png`);

    // JSON-LD: Organization
    setJsonLd("ld-organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: firmName,
      url: SITE_URL,
      logo: `${SITE_URL}/logo-petrocontrol.png`,
      description:
        "Gestión integral de permisos de autoconsumo CNE, controles volumétricos SAT y suministro de combustible para empresas del sector energético en México.",
      telephone: phone,
      email: email,
      address: {
        "@type": "PostalAddress",
        streetAddress: address1,
        addressLocality: address2.split(",")[0]?.trim() || "Guadalajara",
        addressRegion: "Jalisco",
        addressCountry: "MX",
      },
      sameAs: [],
      contactPoint: {
        "@type": "ContactPoint",
        telephone: phone,
        contactType: "customer service",
        availableLanguage: ["Spanish", "English"],
        areaServed: "MX",
      },
    });

    // JSON-LD: LocalBusiness (for local SEO)
    setJsonLd("ld-local-business", {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: firmName,
      image: `${SITE_URL}/logo-petrocontrol.png`,
      url: SITE_URL,
      telephone: phone,
      email: email,
      description:
        "Empresa especializada en gestión de permisos de autoconsumo ante la CNE, implementación de controles volumétricos para el SAT y coordinación de suministro de combustible con comercializadores autorizados.",
      address: {
        "@type": "PostalAddress",
        streetAddress: address1,
        addressLocality: address2.split(",")[0]?.trim() || "Guadalajara",
        addressRegion: "Jalisco",
        postalCode: "44670",
        addressCountry: "MX",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 20.6847,
        longitude: -103.3947,
      },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "18:00",
        },
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: "Saturday",
          opens: "10:00",
          closes: "14:00",
        },
      ],
      priceRange: "$$",
      areaServed: {
        "@type": "Country",
        name: "México",
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Servicios de Regulación Energética",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Gestión Integral de Permisos y Cumplimiento",
              description:
                "Servicio completo que incluye gestión de permisos de autoconsumo, controles volumétricos SAT y suministro de combustible.",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Permisos de Autoconsumo CNE",
              description:
                "Gestión especializada de permisos CNE para empresas que necesitan operar estaciones de combustible en sus instalaciones.",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Controles Volumétricos SAT",
              description:
                "Implementación y certificación de sistemas de medición para cumplimiento de obligaciones fiscales ante el SAT.",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Suministro de Combustible",
              description:
                "Conexión con comercializadores autorizados por la CNE para suministro legal y competitivo de combustible.",
            },
          },
        ],
      },
    });

    // JSON-LD: WebSite with SearchAction (sitelinks searchbox)
    setJsonLd("ld-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: firmName,
      url: SITE_URL,
      description:
        "Gestión de permisos de autoconsumo CNE, controles volumétricos SAT y suministro de combustible en México.",
      inLanguage: "es-MX",
      publisher: {
        "@type": "Organization",
        name: firmName,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/logo-petrocontrol.png`,
        },
      },
    });

    // JSON-LD: BreadcrumbList for service pages
    if (pathname.startsWith("/services/")) {
      const serviceName = pathname.split("/services/")[1]?.replace(/-/g, " ") || "Servicio";
      setJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Servicios", item: `${SITE_URL}/#practice-areas` },
          {
            "@type": "ListItem",
            position: 3,
            name: serviceName.charAt(0).toUpperCase() + serviceName.slice(1),
            item: `${SITE_URL}${pathname}`,
          },
        ],
      });
    } else {
      // Remove breadcrumb if not on service page
      const bcEl = document.getElementById("ld-breadcrumb");
      if (bcEl) bcEl.remove();
    }

    // JSON-LD: FAQ on homepage
    if (pathname === "/") {
      setJsonLd("ld-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "¿Qué empresas necesitan permiso de autoconsumo de la CNE?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Cualquier empresa que almacene combustible para uso propio: industria manufacturera, transporte, agricultura, minería, construcción, entre otras. El permiso es obligatorio para operar estaciones de combustible en instalaciones propias.",
            },
          },
          {
            "@type": "Question",
            name: "¿Cuánto tiempo toma obtener un permiso de autoconsumo?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "El tiempo promedio es de 3 a 4 meses, dependiendo de la complejidad del caso y si la documentación está completa. PETROCONTROL gestiona todo el proceso ante la CNE.",
            },
          },
          {
            "@type": "Question",
            name: "¿Es obligatorio tener controles volumétricos para el SAT?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Sí, desde 2018 es obligatorio para todos los permisionarios de hidrocarburos reportar al SAT mediante sistemas de controles volumétricos certificados. Los reportes son mensuales.",
            },
          },
          {
            "@type": "Question",
            name: "¿Qué es el Programa de Autoconsumo de CNE?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Es el programa de la Comisión Nacional de Energía que permite a empresas operar estaciones de combustible en sus propias instalaciones para uso propio, cumpliendo con la regulación energética vigente.",
            },
          },
          {
            "@type": "Question",
            name: "¿Qué pasa si opero sin permiso de autoconsumo?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Operar sin permiso puede resultar en sanciones económicas significativas y clausura de instalaciones. PETROCONTROL puede ayudar a regularizar la situación de manera expedita.",
            },
          },
        ],
      });
    } else {
      const faqEl = document.getElementById("ld-faq");
      if (faqEl) faqEl.remove();
    }
  }, [title, description, firmName, pathname, phone, email, address1, address2]);

  return null;
}
